/** Passive GA4 / GTM dataLayer reader and client-side payload sanitizer. */

const EVENT_TYPE_RE = /^[a-z][a-z0-9_]{1,47}$/;
const PAYLOAD_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE_RE =
  /(?:^\+\d[\d\s().-]{6,18}\d$)|(?:(?<!\w)(?:\+?\d{1,3}[\s.-]?)?\(\d{3}\)[\s.-]?\d{3}[\s.-]\d{4}(?!\w))|(?:(?<!\w)(?:\+?\d{1,3}[\s.-])?\d{3}[\s.-]\d{3}[\s.-]\d{4}(?!\w))/;

const DEDUPE_WINDOW_MS = 2_000;
const POLL_INTERVAL_MS = 2_000;
const MAX_KEYS = 20;
const MAX_ITEMS = 20;
const MAX_STRING_LENGTH = 200;
const MAX_VALUE = 9_999_999_999.99;

export const GA4_EVENT_TYPES = new Set<string>([
  "add_payment_info",
  "add_shipping_info",
  "add_to_cart",
  "add_to_wishlist",
  "begin_checkout",
  "close_convert_lead",
  "close_unconvert_lead",
  "disqualify_lead",
  "generate_lead",
  "join_group",
  "login",
  "purchase",
  "qualify_lead",
  "refund",
  "remove_from_cart",
  "search",
  "select_content",
  "select_item",
  "select_promotion",
  "share",
  "sign_up",
  "view_cart",
  "view_item",
  "view_item_list",
  "view_promotion",
  "view_search_results",
  "working_lead",
]);

const NUMERIC_COERCE_KEYS = new Set<string>([
  "value",
  "price",
  "tax",
  "shipping",
  "discount",
  "quantity",
  "index",
]);

const IGNORED_META_KEYS = new Set<string>([
  "event",
  "ecommerce",
  "eventModel",
  "send_to",
  "event_callback",
  "event_timeout",
  "non_interaction",
  "transport_type",
  "target_type",
  "target_id",
]);

const PII_EXACT_KEYS = new Set<string>([
  "user_data",
  "customer",
  "email",
  "e_mail",
  "email_address",
  "phone",
  "phone_number",
  "tel",
  "telephone",
  "mobile",
  "name",
  "first_name",
  "last_name",
  "full_name",
  "given_name",
  "family_name",
  "user_name",
  "username",
  "address",
  "street",
  "street_address",
  "city",
  "state",
  "region",
  "zip",
  "zipcode",
  "zip_code",
  "postal",
  "postal_code",
  "postcode",
  "ip",
  "ip_address",
  "client_ip",
  "token",
  "access_token",
  "refresh_token",
  "secret",
  "password",
  "passwd",
  "ssn",
  "tax_id",
  "card",
  "card_number",
  "cc_number",
  "credit_card",
  "cvv",
  "cvc",
  "iban",
  "account_number",
  "message",
  "text",
  "body",
  "comment",
  "notes",
]);

const PII_KEY_SUBSTRINGS = [
  "email",
  "phone",
  "password",
  "passwd",
  "secret",
  "token",
  "ssn",
  "credit_card",
  "card_number",
  "user_data",
];

export interface ParsedDataLayerEvent {
  type: string;
  payload: Record<string, unknown>;
}

type DataLayerCallback = (event: ParsedDataLayerEvent) => void;

let activeCallback: DataLayerCallback | null = null;
let seenEntries = new WeakSet<object>();
let seenTransactions = new Set<string>();
let recentSignatures = new Map<string, number>();

export function isValidEventType(type: unknown): type is string {
  return typeof type === "string" && EVENT_TYPE_RE.test(type);
}

export function isPiiKey(key: string): boolean {
  if (!PAYLOAD_KEY_RE.test(key) || IGNORED_META_KEYS.has(key)) return true;
  const lowered = key.toLowerCase();
  if (PII_EXACT_KEYS.has(lowered)) return true;
  return PII_KEY_SUBSTRINGS.some((sub) => lowered.includes(sub));
}

export function sanitizeUrlValue(raw: string): string | null {
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    const kept = new URLSearchParams();
    for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
      const val = parsed.searchParams.get(key);
      if (val) kept.set(key, val.slice(0, 255));
    }
    const qs = kept.toString();
    const cleaned = `${parsed.origin}${parsed.pathname || "/"}${
      qs ? `?${qs}` : ""
    }`;
    return cleaned.slice(0, MAX_STRING_LENGTH);
  } catch {
    return null;
  }
}

function stripControls(value: string): string {
  let out = "";
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code >= 32 && code !== 127) out += value[i];
  }
  return out;
}

function sanitizeScalar(key: string, value: unknown): unknown {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || Math.abs(value) > MAX_VALUE) {
      return undefined;
    }
    return Math.round(value * 10_000) / 10_000;
  }
  if (typeof value === "string") {
    const cleaned = stripControls(value).trim();
    if (!cleaned) return undefined;
    if (NUMERIC_COERCE_KEYS.has(key.toLowerCase())) {
      const numeric = Number(cleaned);
      if (Number.isFinite(numeric) && Math.abs(numeric) <= MAX_VALUE) {
        return Math.round(numeric * 10_000) / 10_000;
      }
    }
    const lowered = cleaned.toLowerCase();
    if (lowered.startsWith("http://") || lowered.startsWith("https://")) {
      return sanitizeUrlValue(cleaned) ?? undefined;
    }
    if (EMAIL_RE.test(cleaned) || PHONE_RE.test(cleaned)) {
      return undefined;
    }
    return cleaned.slice(0, MAX_STRING_LENGTH);
  }
  return undefined;
}

function sanitizeNestedObject(
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (Object.keys(cleaned).length >= MAX_KEYS) break;
    if (isPiiKey(key)) continue;
    const scalar = sanitizeScalar(key, value);
    if (scalar !== undefined) cleaned[key] = scalar;
  }
  return cleaned;
}

export function sanitizeEventPayload(
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (Object.keys(cleaned).length >= MAX_KEYS) break;
    if (isPiiKey(key) || value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      const list: unknown[] = [];
      for (const item of value.slice(0, MAX_ITEMS)) {
        if (item && typeof item === "object" && !Array.isArray(item)) {
          const nested = sanitizeNestedObject(item as Record<string, unknown>);
          if (Object.keys(nested).length > 0) list.push(nested);
        } else {
          const scalar = sanitizeScalar(key, item);
          if (scalar !== undefined) list.push(scalar);
        }
      }
      if (list.length > 0) cleaned[key] = list;
    } else if (typeof value === "object") {
      const nested = sanitizeNestedObject(value as Record<string, unknown>);
      if (Object.keys(nested).length > 0) cleaned[key] = nested;
    } else {
      const scalar = sanitizeScalar(key, value);
      if (scalar !== undefined) cleaned[key] = scalar;
    }
  }
  return cleaned;
}

function isArgumentsOrTuple(entry: unknown): entry is ArrayLike<unknown> {
  if (!entry || typeof entry !== "object") return false;
  if (Array.isArray(entry)) return true;
  const record = entry as Record<string, unknown>;
  return (
    typeof record.length === "number" &&
    record.length >= 2 &&
    typeof record[0] === "string"
  );
}

/** Extract a normalized standard GA4 event from a dataLayer entry or gtag Arguments object. */
export function parseDataLayerEntry(
  entry: unknown,
): ParsedDataLayerEvent | null {
  if (!entry || typeof entry !== "object") return null;

  let eventName: string | null = null;
  const mergedParams: Record<string, unknown> = {};

  if (isArgumentsOrTuple(entry)) {
    if (entry[0] !== "event" || typeof entry[1] !== "string") return null;
    eventName = entry[1].trim();
    const rawParams = entry[2];
    if (
      rawParams &&
      typeof rawParams === "object" &&
      !Array.isArray(rawParams)
    ) {
      const paramsObj = rawParams as Record<string, unknown>;
      if (
        paramsObj.ecommerce &&
        typeof paramsObj.ecommerce === "object" &&
        !Array.isArray(paramsObj.ecommerce)
      ) {
        Object.assign(
          mergedParams,
          paramsObj.ecommerce as Record<string, unknown>,
        );
      }
      Object.assign(mergedParams, paramsObj);
    }
  } else {
    const record = entry as Record<string, unknown>;
    if (typeof record.event !== "string") return null;
    eventName = record.event.trim();
    if (
      record.ecommerce &&
      typeof record.ecommerce === "object" &&
      !Array.isArray(record.ecommerce)
    ) {
      Object.assign(mergedParams, record.ecommerce as Record<string, unknown>);
    }
    if (
      record.eventModel &&
      typeof record.eventModel === "object" &&
      !Array.isArray(record.eventModel)
    ) {
      const model = record.eventModel as Record<string, unknown>;
      if (
        model.ecommerce &&
        typeof model.ecommerce === "object" &&
        !Array.isArray(model.ecommerce)
      ) {
        Object.assign(mergedParams, model.ecommerce as Record<string, unknown>);
      }
      Object.assign(mergedParams, model);
    }
    for (const [key, value] of Object.entries(record)) {
      if (key.startsWith("gtm.") || IGNORED_META_KEYS.has(key)) continue;
      mergedParams[key] = value;
    }
  }

  if (!eventName || eventName.startsWith("chatbot_")) return null;
  if (!GA4_EVENT_TYPES.has(eventName)) return null;

  return {
    type: eventName,
    payload: sanitizeEventPayload(mergedParams),
  };
}

function isDuplicate(parsed: ParsedDataLayerEvent, now: number): boolean {
  const txId = parsed.payload.transaction_id;
  if (
    (parsed.type === "purchase" || parsed.type === "refund") &&
    (typeof txId === "string" || typeof txId === "number") &&
    String(txId).trim() !== ""
  ) {
    const txKey = `${parsed.type}:tx:${String(txId).trim()}`;
    if (seenTransactions.has(txKey)) return true;
    seenTransactions.add(txKey);
    return false;
  }

  for (const [sig, timestamp] of recentSignatures.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentSignatures.delete(sig);
    }
  }

  const signature = `${parsed.type}:${JSON.stringify(parsed.payload)}`;
  const lastSeen = recentSignatures.get(signature);
  if (lastSeen !== undefined && now - lastSeen <= DEDUPE_WINDOW_MS) {
    return true;
  }
  recentSignatures.set(signature, now);
  return false;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let scanning = false;

function handleEntry(entry: unknown): void {
  if (!activeCallback || !entry || typeof entry !== "object") return;
  if (seenEntries.has(entry)) return;
  seenEntries.add(entry);
  try {
    const parsed = parseDataLayerEntry(entry);
    if (!parsed) return;
    if (isDuplicate(parsed, Date.now())) return;
    activeCallback(parsed);
  } catch {
    // A malformed host entry must not break the scan or the widget.
  }
}

function readLayer(): ArrayLike<unknown> | null {
  let layer: unknown;
  try {
    layer = window.dataLayer;
  } catch {
    return null;
  }
  if (Array.isArray(layer)) return layer;
  if (
    layer &&
    typeof layer === "object" &&
    typeof (layer as { length?: unknown }).length === "number"
  ) {
    return layer as ArrayLike<unknown>;
  }
  return null;
}

/** Read whatever is currently in `window.dataLayer`. Does not modify it. */
export function scanDataLayer(): void {
  if (!activeCallback || scanning) return;
  scanning = true;
  try {
    const layer = readLayer();
    if (!layer) return;
    const length = layer.length;
    for (let i = 0; i < length; i += 1) {
      handleEntry(layer[i]);
    }
  } catch {
    // A hostile dataLayer getter or length must not escape the scan.
  } finally {
    scanning = false;
  }
}

/** Scan now. Called before a flush so a send includes events already queued in the array. */
export function ensureDataLayerListener(): void {
  scanDataLayer();
}

export function installDataLayerListener(onEvent: DataLayerCallback): void {
  activeCallback = onEvent;
  if (pollTimer === null) {
    pollTimer = setInterval(() => {
      scanDataLayer();
    }, POLL_INTERVAL_MS);
  }
  scanDataLayer();
}

/** Stop reading the host dataLayer. Used when tracking is turned off. */
export function stopDataLayerListener(): void {
  activeCallback = null;
  scanning = false;
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

export function resetDataLayerForTests(): void {
  seenEntries = new WeakSet<object>();
  seenTransactions = new Set<string>();
  recentSignatures = new Map<string, number>();
  stopDataLayerListener();
}
